import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { api } from '../api/client'
import type { CraReport, CraStep } from '../api/types'
import { usePermessiModuli } from '../auth/permessi'
import { Card, ErrorBanner, PageSection, Spinner } from '../components/common'
import { BadgePasso, contenutoPasso, daLocale, etichettaPasso, locale, quandoCra } from '../components/Cra'
import { DataTable } from '../components/DataTable'
import { Finestra } from '../components/ModificheGitlab'
import { nonLicenziato, scaricaJson } from '../components/Sbom'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * One art. 14 report of the CRA register: the moment of awareness (confirmed
 * by a person: the clocks start there; when CENSO saw it is shown apart), the
 * steps with their deadlines and what each must contain, the corrective
 * measure (the final report's 14 days start there), the intermediate report
 * when the CSIRT asks, the users' advisory (CSAF), the timeline. See cra_service.
 */

const MISURE: Array<[string, () => string]> = [
  ['vendor_fix', () => t('Aggiornamento del fabbricante')], ['mitigation', () => t('Mitigazione')],
  ['workaround', () => t('Rimedio provvisorio')], ['none_available', () => t('Nessuna misura disponibile')],
  ['no_fix_planned', () => t('Nessuna correzione prevista')],
]

const EVENTI: Record<string, () => string> = {
  opened: () => t('segnalazione aperta'), aware_confirmed: () => t('presa di conoscenza confermata'),
  aware_corrected: () => t('presa di conoscenza corretta'), mitigation: () => t('misura correttiva registrata'),
  intermediate_requested: () => t('il CSIRT chiede una relazione intermedia'), closed: () => t('segnalazione chiusa'),
}

function evento(e: string, tipo: CraReport['kind']): string {
  if (e.startsWith('step:')) return t('inviata: {p}', { p: etichettaPasso(e.slice(5), tipo) })
  return EVENTI[e]?.() ?? e
}

export default function CraSegnalazione() {
  const { id = '' } = useParams()
  const dati = useAsync(() => api.craReport(id), [id])
  const r = dati.data
  if (dati.error && nonLicenziato(dati.error)) return <Card><p>{t('Non compreso nella licenza')}</p></Card>
  return (
    <>
      <header className="page__header">
        <div>
          <p className="field__hint" style={{ margin: 0 }}><Link to="/cra?vista=segnalazioni">{t('CRA Compliance')} · {t('Segnalazioni')}</Link></p>
          <h1 className="page__title">{r ? `${r.reference} — ${r.title}` : t('Segnalazione')}</h1>
          {r && (
            <p className="page__subtitle">
              <span className={`badge ${r.status === 'open' ? 'badge--info' : 'badge--muted'}`}>{r.status === 'open' ? t('aperta') : t('chiusa')}</span>{' '}
              {r.kind === 'incident' ? t('Incidente grave sulla sicurezza del prodotto (art. 14(4))') : t('Vulnerabilità sfruttata attivamente (art. 14(2))')}
              {' · '}<Link to={`/cra/products/${r.product_id}`}>{r.product}</Link>
              {r.releases.length > 0 && <> · {t('rilasci')} <span className="mono">{r.releases.join(', ')}</span></>}
            </p>
          )}
        </div>
      </header>
      <ErrorBanner error={dati.error} />
      {dati.loading && !r ? <Spinner /> : r && <Dettaglio r={r} onCambiato={() => void dati.reload()} />}
    </>
  )
}

function Dettaglio({ r, onCambiato }: { r: CraReport; onCambiato: () => void }) {
  const scrive = usePermessiModuli().scrive('cra') && r.status === 'open'
  const [passo, setPasso] = useState<CraStep | null>(null)
  const [chiudi, setChiudi] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  const fai = async (f: () => Promise<CraReport>) => {
    setErrore(null)
    try { await f(); onCambiato() } catch (e) { setErrore(e) }
  }

  async function csaf() {
    setErrore(null)
    try { await scaricaJson(api.craReportCsafPath(r.id), `${r.reference}.csaf.json`) } catch (e) { setErrore(e) }
  }

  return (
    <>
      <ErrorBanner error={errore} />
      <PageSection id="consapevolezza">
        <Consapevolezza r={r} scrive={scrive} onConferma={(q) => void fai(() => api.craReportAware(r.id, q))} />
      </PageSection>
      <PageSection id="passi">
        <Card title={t('Comunicazioni')}>
          <p className="field__hint" style={{ marginTop: 0 }}>
            {r.role === 'importer' || r.role === 'distributor'
              ? t('Come importatore o distributore: informare il fabbricante senza indebito ritardo e, se il rischio è significativo, le autorità di vigilanza del mercato.')
              : t('Dalla piattaforma unica di ENISA, al CSIRT coordinatore{c} e a ENISA. Registra qui ogni invio con il riferimento ricevuto.', { c: r.csirt ? ` (${r.csirt})` : '' })}
          </p>
          <DataTable rows={r.steps} rowKey={(s) => s.step} paginazione={false} strumenti={false} columns={[
            { key: 'step', header: t('Passo'), value: (s) => etichettaPasso(s.step, r.kind) },
            { key: 'due_at', header: t('Entro'), value: (s) => s.due_at ?? '', render: (s) => quandoCra(s.due_at) },
            { key: 'state', header: t('Stato'), value: (s) => s.state, render: (s) => <BadgePasso p={s} /> },
            { key: 'done', header: t('Inviata'), value: (s) => s.done_at ?? '', render: (s) => s.done_at
              ? <small>{quandoCra(s.done_at)} · {s.done_by}{s.reference && <> · <span className="mono">{s.reference}</span></>}</small> : null },
            { key: 'azione', header: '', filter: false, sort: false, render: (s) => !s.done_at && s.state !== 'on_request' && (
              <button type="button" className={s.state === 'overdue' || s.state === 'due' ? '' : 'secondary'} disabled={!scrive}
                onClick={() => setPasso(s)}>{t('Registra l’invio')}</button>) },
          ]} />
          <div className="toolbar">
            {r.role !== 'importer' && r.role !== 'distributor' && !r.intermediate_due_at && (
              <RichiestaIntermedia scrive={scrive} onConferma={(q) => void fai(() => api.craReportIntermediate(r.id, q))} />
            )}
            <button type="button" className="secondary" onClick={() => void csaf()}>{t('Scarica l’avviso CSAF per gli utenti')}</button>
          </div>
        </Card>
      </PageSection>
      {r.role !== 'importer' && r.role !== 'distributor' && (
        <PageSection id="misura"><Misura r={r} scrive={scrive} onSalva={(m) => void fai(() => api.craReportMitigation(r.id, m))} /></PageSection>
      )}
      <PageSection id="cronologia">
        <Card title={t('Cronologia')}>
          {r.detected_at && <p className="field__hint" style={{ marginTop: 0 }}>{t('CENSO l’ha vista per la prima volta il {q}.', { q: quandoCra(r.detected_at) })}</p>}
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {r.timeline.slice().reverse().map((e, i) => (
              <li key={i}><small>{quandoCra(e.at)} · {e.by} — {evento(e.event, r.kind)}{e.detail && e.event !== 'aware_confirmed' ? `: ${e.detail}` : ''}</small></li>
            ))}
          </ul>
          {r.status === 'open' && (
            <div className="toolbar"><button type="button" className="secondary" disabled={!scrive} onClick={() => setChiudi(true)}>{t('Chiudi la segnalazione')}</button></div>
          )}
        </Card>
      </PageSection>
      {passo && <FinestraPasso r={r} s={passo} onChiudi={() => setPasso(null)}
        onSalva={(d) => { setPasso(null); void fai(() => api.craReportStep(r.id, passo.step, d)) }} />}
      {chiudi && <FinestraChiusura r={r} onChiudi={() => setChiudi(false)} onSalva={(m) => { setChiudi(false); void fai(() => api.craReportClose(r.id, m)) }} />}
    </>
  )
}

function Consapevolezza({ r, scrive, onConferma }: { r: CraReport; scrive: boolean; onConferma: (iso: string) => void }) {
  const [quando, setQuando] = useState(locale(r.detected_at ?? null))
  if (r.aware_at) {
    return (
      <Card title={t('Presa di conoscenza')}>
        <p style={{ margin: 0 }}>{quandoCra(r.aware_at)} · {t('confermata da {chi}', { chi: r.aware_confirmed_by })}
          {r.malicious !== null && r.kind === 'incident' && <> · {r.malicious ? t('sospetto di atti illeciti o malevoli') : t('nessun sospetto di atti malevoli')}</>}</p>
      </Card>
    )
  }
  return (
    <Card title={t('Presa di conoscenza')}>
      <div className="banner banner--warn">{t('I termini non sono ancora partiti: partono dal momento in cui l’organizzazione è venuta a conoscenza della vulnerabilità sfruttata o dell’incidente. Lo conferma una persona.')}</div>
      {r.detected_at && <p className="field__hint">{t('CENSO l’ha vista per la prima volta il {q}: non è per forza il momento della presa di conoscenza, ma non può essere molto dopo.', { q: quandoCra(r.detected_at) })}</p>}
      <div className="toolbar" style={{ alignItems: 'flex-end' }}>
        <div className="field" style={{ minWidth: 240 }}>
          <label htmlFor="aware">{t('Presa di conoscenza')}</label>
          <input id="aware" type="datetime-local" value={quando} onChange={(e) => setQuando(e.target.value)} />
        </div>
        <button type="button" disabled={!scrive} onClick={() => onConferma(daLocale(quando))}>{t('Conferma: partono i termini')}</button>
      </div>
    </Card>
  )
}

function RichiestaIntermedia({ scrive, onConferma }: { scrive: boolean; onConferma: (iso: string) => void }) {
  const [aperta, setAperta] = useState(false)
  const [entro, setEntro] = useState(locale(new Date(Date.now() + 7 * 864e5).toISOString()))
  if (!aperta) return <button type="button" className="secondary" disabled={!scrive} onClick={() => setAperta(true)}>{t('Il CSIRT chiede una relazione intermedia')}</button>
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'flex-end' }}>
      <span className="field" style={{ margin: 0 }}>
        <label htmlFor="intermedia">{t('Entro')}</label>
        <input id="intermedia" type="datetime-local" value={entro} onChange={(e) => setEntro(e.target.value)} />
      </span>
      <button type="button" onClick={() => onConferma(daLocale(entro))}>{t('Registra la richiesta')}</button>
    </span>
  )
}

function Misura({ r, scrive, onSalva }: { r: CraReport; scrive: boolean; onSalva: (m: { at: string; category: string; description: string; fixed_version: string }) => void }) {
  const m = r.mitigation
  const [categoria, setCategoria] = useState(m.category ?? 'vendor_fix')
  const [descrizione, setDescrizione] = useState(m.description ?? '')
  const [versione, setVersione] = useState(m.fixed_version ?? '')
  const [quando, setQuando] = useState(locale(m.at ?? null))
  return (
    <Card title={t('Misura correttiva o di mitigazione')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {r.kind === 'vulnerability'
          ? t('Quando la misura è disponibile partono i 14 giorni del rapporto finale (art. 14(2)(c)); la versione corretta compare nell’avviso CSAF per gli utenti.')
          : t('Le misure prese e quelle che gli utenti possono prendere vanno nella notifica e nel rapporto finale.')}
      </p>
      {m.at && <p>{t('Registrata il {q} da {chi}', { q: quandoCra(m.at), chi: m.by ?? '' })}</p>}
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        <div className="field" style={{ minWidth: 220 }}>
          <label htmlFor="mis-cat">{t('Tipo')}</label>
          <select id="mis-cat" value={categoria} disabled={!scrive} onChange={(e) => setCategoria(e.target.value)}>
            {MISURE.map(([k, l]) => <option key={k} value={k}>{l()}</option>)}
          </select>
        </div>
        <div className="field" style={{ minWidth: 180 }}>
          <label htmlFor="mis-ver">{t('Versione corretta')}</label>
          <input id="mis-ver" type="text" className="mono" value={versione} disabled={!scrive} onChange={(e) => setVersione(e.target.value)} />
        </div>
        <div className="field" style={{ minWidth: 220 }}>
          <label htmlFor="mis-quando">{t('Disponibile dal')}</label>
          <input id="mis-quando" type="datetime-local" value={quando} disabled={!scrive} onChange={(e) => setQuando(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="mis-desc">{t('Che cosa fare')}</label>
        <textarea id="mis-desc" rows={3} value={descrizione} disabled={!scrive} onChange={(e) => setDescrizione(e.target.value)}
          placeholder={t('Per esempio: aggiornare alla 1.0.1, che porta log4j-core 2.17.1; in alternativa togliere la classe JndiLookup.')} />
      </div>
      <div className="toolbar"><button type="button" disabled={!scrive || descrizione.trim().length < 10}
        onClick={() => onSalva({ at: daLocale(quando), category: categoria, description: descrizione, fixed_version: versione })}>{m.at ? t('Aggiorna la misura') : t('Registra la misura')}</button></div>
    </Card>
  )
}

function FinestraPasso({ r, s, onChiudi, onSalva }: {
  r: CraReport
  s: CraStep
  onChiudi: () => void
  onSalva: (d: { at: string; reference: string; note: string }) => void
}) {
  const [quando, setQuando] = useState(locale(null))
  const [riferimento, setRiferimento] = useState('')
  const [nota, setNota] = useState('')
  const finaleSenzaMisura = s.step === 'final_report' && r.kind === 'vulnerability' && !r.mitigation.at
  return (
    <Finestra titolo={t('Registra l’invio')} sotto={`${r.reference} · ${etichettaPasso(s.step, r.kind)}`} onChiudi={onChiudi}>
      {contenutoPasso(s.step, r.kind) && <div className="banner banner--info">{t('Che cosa deve contenere')}: {contenutoPasso(s.step, r.kind)}</div>}
      {s.step === 'early_warning' && r.member_states.length > 0 && <p>{t('Stati membri in cui il prodotto è disponibile')}: <strong>{r.member_states.join(', ')}</strong></p>}
      {finaleSenzaMisura && <div className="banner banner--warn">{t('Il rapporto finale segue la misura correttiva o di mitigazione: registrala prima.')}</div>}
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        <div className="field" style={{ minWidth: 240 }}>
          <label htmlFor="passo-quando">{t('Inviata il')}</label>
          <input id="passo-quando" type="datetime-local" value={quando} onChange={(e) => setQuando(e.target.value)} />
        </div>
        <div className="field" style={{ flex: 1, minWidth: 220 }}>
          <label htmlFor="passo-rif">{t('Riferimento ricevuto')}</label>
          <input id="passo-rif" type="text" className="mono" value={riferimento} onChange={(e) => setRiferimento(e.target.value)}
            placeholder={t('dalla piattaforma ENISA o dal CSIRT')} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="passo-nota">{t('Nota')}</label>
        <textarea id="passo-nota" rows={3} value={nota} onChange={(e) => setNota(e.target.value)} />
      </div>
      <div className="toolbar"><button type="button" disabled={finaleSenzaMisura} onClick={() => onSalva({ at: daLocale(quando), reference: riferimento, note: nota })}>{t('Registra')}</button></div>
    </Finestra>
  )
}

function FinestraChiusura({ r, onChiudi, onSalva }: { r: CraReport; onChiudi: () => void; onSalva: (motivo: string) => void }) {
  const [motivo, setMotivo] = useState('')
  const ultimo = r.role === 'importer' || r.role === 'distributor' ? 'manufacturer_informed' : 'final_report'
  const completa = r.steps.some((s) => s.step === ultimo && s.done_at)
  return (
    <Finestra titolo={t('Chiudi la segnalazione')} sotto={r.reference} onChiudi={onChiudi}>
      {!completa && <div className="banner banner--warn">{t('Manca l’ultimo passo ({p}): per chiudere lo stesso, scrivi perché.', { p: etichettaPasso(ultimo, r.kind) })}</div>}
      <div className="field">
        <label htmlFor="chiusura">{t('Motivo o nota di chiusura')}</label>
        <textarea id="chiusura" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </div>
      <div className="toolbar"><button type="button" disabled={!completa && motivo.trim().length < 10} onClick={() => onSalva(motivo)}>{t('Chiudi')}</button></div>
    </Finestra>
  )
}
